import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card, Center, Loader, Stack, Table, Text } from '@mantine/core';
import { IconProgressCheck } from '@tabler/icons-react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { i18nKeys, useTranslation } from 'locales';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { prop } from 'remeda';

import { type LightAutomation, useLoadAutomations, useUpdateAutomationOrder } from '../../../api';

import { DraggableRow } from './components';
import { useAutomationTableColumns } from './hooks';

type AutomationsTableProps = {
  handleEditAutomation: (id: string) => void;
  handleDeleteAutomation: (id: string) => void;
};

export const AutomationsTable = ({
  handleEditAutomation,
  handleDeleteAutomation,
}: AutomationsTableProps) => {
  const history = useHistory();
  const { t } = useTranslation();

  // Local state to allow reordering
  const [data, setData] = useState<Array<LightAutomation>>([]);

  const { automations, isAutomationsInitialLoading } = useLoadAutomations();
  const { updateAutomationOrder } = useUpdateAutomationOrder();

  useEffect(() => setData(automations), [automations]);

  const columns = useAutomationTableColumns(handleEditAutomation, handleDeleteAutomation);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const dataIds = useMemo<UniqueIdentifier[]>(() => data.map(prop('id')), [data]);
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!(active && over && active.id !== over.id)) return;

      setData(currentData => {
        const oldIndex = dataIds.indexOf(active.id);
        const newIndex = dataIds.indexOf(over.id);
        const newData = arrayMove(currentData, oldIndex, newIndex);
        updateAutomationOrder({ order: newData.map(item => item.id) });

        return newData;
      });
    },
    [dataIds, updateAutomationOrder],
  );

  const handleClickRow = (event: React.MouseEvent, rowId: string) => {
    if (event.ctrlKey || event.metaKey) window.open(`/settings/automations/${rowId}`, '_blank');
    else history.push(`/settings/automations/${rowId}`);
  };

  const handleMiddleClickRow = (event: React.MouseEvent, rowId: string) => {
    if (event.button === 1) window.open(`/settings/automations/${rowId}`, '_blank');
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <Card radius={8} p={0}>
        <Table
          highlightOnHover
          highlightOnHoverColor="purple.0"
          horizontalSpacing="md"
          verticalSpacing="space3"
          stickyHeader
          fz={12}
        >
          <Table.Thead>
            {table.getHeaderGroups().map(headerGroup => (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Table.Th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.Th>
                ))}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {/* @ts-ignore*/}
            <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
              {table.getRowModel().rows.map(row => (
                <DraggableRow
                  key={row.id}
                  row={row}
                  handleClickRow={handleClickRow}
                  handleMiddleClickRow={handleMiddleClickRow}
                />
              ))}
            </SortableContext>
          </Table.Tbody>
        </Table>
        {isAutomationsInitialLoading && (
          <Center my="100" w="100%">
            <Loader />
          </Center>
        )}
        {!isAutomationsInitialLoading && data.length === 0 && automations.length === 0 && (
          <Center my="100" w="100%">
            <Stack align="center">
              <IconProgressCheck size={75} color="gray" />
              <Text size="sm">{t(i18nKeys.AUTOMATION.EMPTY_STATE.NO_AUTOMATIONS_FOUND)}</Text>
            </Stack>
          </Center>
        )}
      </Card>
    </DndContext>
  );
};
